import React, {useState, useRef, useEffect, useContext} from 'react';
import {useDescriptionAutocomplete} from '../hooks/useDescriptionAutocomplete';
import {AppContext} from '../context/AppContext';

interface DescriptionAutocompleteInputProps {
  inputId: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}

const DescriptionAutocompleteInput: React.FC<DescriptionAutocompleteInputProps> = ({
  inputId,
  value,
  onChange,
  placeholder = '',
  disabled = false,
  autoFocus = false,
  className = '',
  onFocus: onFocusProp,
  onBlur: onBlurProp
}) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const context = useContext(AppContext);
  if (!context) throw new Error('DescriptionAutocompleteInput must be used within AppContextProvider');
  const { i18n } = context;

  const { recent, others } = useDescriptionAutocomplete(value);
  const flat = [...recent, ...others];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapperRef.current && !wrapperRef.current.contains(t)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Raise the entire card above siblings when the dropdown is open
  useEffect(() => {
    const wrap = wrapperRef.current as HTMLElement | null;
    if (!wrap) return;

    let cardEl: HTMLElement | null = wrap;
    while (cardEl && !cardEl.classList.contains('card')) {
      cardEl = cardEl.parentElement;
    }
    if (!cardEl) return;

    if (open) {
      cardEl.classList.add('dropdown-active');
    } else {
      cardEl.classList.remove('dropdown-active');
    }
    return () => {
      cardEl.classList.remove('dropdown-active');
    };
  }, [open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    onChange(v);
    setOpen(true);
    setHighlight(flat.length ? 0 : -1);
  };

  const handleFocus = () => {
    if (value.trim() || flat.length > 0) {
      setOpen(true);
    }
    if (onFocusProp) {
      onFocusProp();
    }
  };

  const handleBlur = () => {
    setOpen(false);
    if (onBlurProp) {
      onBlurProp();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(0, flat.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && flat[highlight]) {
        e.preventDefault();
        onChange(flat[highlight].description);
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const renderItem = (s: {description: string; usageCount: number}, idx: number) => (
    <div
      key={`${s.description}-${idx}`}
      className={`suggestion-item ${idx === highlight ? 'active' : ''}`}
      onMouseEnter={() => setHighlight(idx)}
      onMouseDown={(e) => {
        e.preventDefault();
        onChange(s.description);
        setOpen(false);
      }}
    >
      <span className="suggestion-text">{s.description}</span>
      <span className="suggestion-meta">{s.usageCount}</span>
    </div>
  );

  let runningIdx = 0;

  return (
    <div ref={wrapperRef} className="description-autocomplete field">
      <input
        id={inputId}
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        autoComplete="off"
        disabled={disabled}
        autoFocus={autoFocus}
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={className}
      />
      <div className={`suggestions ${open ? '' : 'hidden'}`}>
        {open && (
          <>
            {flat.length === 0 && value.trim() && (
              <div className="suggestion-item empty">No suggestions</div>
            )}

            {recent.length > 0 && (
              <>
                <div
                  className="suggestion-label"
                  aria-hidden="true"
                >
                  {i18n.t('recent')}
                </div>
                {recent.map((s) => renderItem(s, runningIdx++))}
                <div className="dropdown-divider"></div>
              </>
            )}

            {others.map((s) => renderItem(s, runningIdx++))}
          </>
        )}
      </div>
    </div>
  );
};

export default DescriptionAutocompleteInput;
